import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { SMBold, SMRegular, SMSemiBold } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { InputText } from '@/components/shared/InputText';
import { BankTransaction } from '@/services/PaymentsServiceV2';
import { useGetTransactionBankDetails } from '@/services/paymentsQuery';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useFormatting } from '@/utils/formatting';
import { useErrorHandling } from '@/utils/errorHandling';
import { useAppDispatch } from '@/lib/hooks';

export interface CancelTransactionModelProps {
    selectedTransaction: BankTransaction | null;
    open: boolean;
    handleClose: () => void;
}

const UI_STATE = {
    LOOK_UP: 'LOOK_UP',
    MATCHED: 'MATCHED'
};

const CancelTransactionModel = ({
    selectedTransaction,
    open,
    handleClose
}: CancelTransactionModelProps) => {
    const { t } = useTranslation();

    const [uiState, setUiState] = useState(UI_STATE.LOOK_UP);
    const [matchId, setMatchId] = useState('');
    const [showTransactionDetails, setShowTransactionDetails] = useState(false);

    const { cancelTransaction } = usePaymentsMutations();

    const [selectedTransactionId, setSelectedTransactionId] = useState('');

    useEffect(() => {
        if (selectedTransaction) {
            setSelectedTransactionId(selectedTransaction.transactionId!);
        }
    }, [selectedTransaction]);

    const { data, isError, isSuccess } = useGetTransactionBankDetails(
        selectedTransactionId,
        open && selectedTransactionId != null
    );

    const _dispatch = useAppDispatch();
    const { setAlert } = useNotificationsSnackbar();

    useEffect(() => {
        let debounceObj: any;
        if (uiState == UI_STATE.LOOK_UP && matchId?.length > 0) {
            debounceObj = setTimeout(() => {
                setSelectedTransactionId(matchId);
            }, 1000);
        }
        return () => {
            clearTimeout(debounceObj);
        };
    }, [matchId, uiState]);

    useEffect(() => {
        if (isSuccess) {
            setAlert(
                t('transaction_monitoring.transaction_cancelled'),
                'success'
            );

            handleClose?.();
        }
    }, [cancelTransaction.isSuccess]);

    // useEffect(() => {
    //     dispatch(setLoader(isPending));
    // }, [isPending]);

    useEffect(() => {
        if (isSuccess) {
            setUiState(UI_STATE.MATCHED);
            setMatchId(data?.data?.transactionId);
        }
        setShowTransactionDetails(isSuccess);
    }, [data, isSuccess]);

    const { backendErrorHandler } = useErrorHandling();

    useEffect(() => {
        if (cancelTransaction.error) {
            backendErrorHandler()(cancelTransaction.error);
        }
    }, [cancelTransaction.error]);

    useEffect(() => {
        if (isError || !selectedTransaction?.transactionId!) {
            setUiState(UI_STATE.LOOK_UP);
            setMatchId('');
            setShowTransactionDetails(false);
        }
    }, [isError, selectedTransaction?.transactionId!]);

    const { getBalanceLabel, formatDate } = useFormatting();

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            aria-labelledby="ignore-transaction-title"
            aria-describedby="ignore-transaction-description"
        >
            <StyledDialogContainer
                style={{
                    width: '400px',
                    padding: '24px'
                }}
            >
                <Box m={1} mb={2} textAlign="center" width={'100%'}>
                    <SMSemiBold mb={1}>
                        {t('transaction_monitoring.cancel_transaction')}
                    </SMSemiBold>
                    <SMRegular>
                        {selectedTransaction?.bankTransactionRecordId}
                    </SMRegular>
                </Box>

                <InputText
                    label={
                        uiState == UI_STATE.LOOK_UP
                            ? 'Enter a transaction ID to match'
                            : 'Transaction ID'
                    }
                    variant="outlined"
                    disabled={uiState === UI_STATE.MATCHED}
                    allowDisabledEdit
                    onDisabledEditClick={() => {
                        setUiState(UI_STATE.LOOK_UP);
                        setMatchId('');
                        setShowTransactionDetails(false);
                    }}
                    value={matchId}
                    error={isError}
                    helperText={
                        isError ? (
                            <SMRegular>
                                The entered transaction ID could not be found
                            </SMRegular>
                        ) : (
                            ''
                        )
                    }
                    onChange={(e) => setMatchId(e.target.value)}
                />

                {showTransactionDetails && data?.data && (
                    <Grid container mt={2}>
                        <Grid container>
                            <Grid xs={4} item textAlign={'end'}>
                                <SMSemiBold>
                                    {t('transaction_monitoring.company_name')}
                                </SMSemiBold>
                            </Grid>
                            <Grid xs={8} item textAlign={'end'}>
                                {data?.data?.companyName}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid xs={4} item textAlign={'end'}>
                                <SMSemiBold>
                                    {t('transaction_monitoring.employee_name')}
                                </SMSemiBold>
                            </Grid>
                            <Grid xs={8} item textAlign={'end'}>
                                {data?.data?.userName}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid xs={4} item textAlign={'end'}>
                                <SMSemiBold>
                                    {t('transaction_monitoring.amount')}
                                </SMSemiBold>
                            </Grid>
                            <Grid xs={8} item textAlign={'end'}>
                                {getBalanceLabel(
                                    data?.data?.amount,
                                    data?.data?.currency
                                )}
                            </Grid>
                        </Grid>

                        <Grid container>
                            <Grid xs={4} item textAlign={'end'}>
                                <SMSemiBold>
                                    {t('transaction_monitoring.date')}
                                </SMSemiBold>
                            </Grid>
                            <Grid xs={8} item textAlign={'end'}>
                                {formatDate(
                                    data?.data?.createdAt,
                                    'DD. MMM HH:mm'
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 3
                    }}
                    gap={1.5}
                >
                    <OutlinedSecondaryButtonLG
                        fullWidth
                        color="secondary"
                        style={{
                            height: '40px',
                            borderRadius: '8px'
                        }}
                        onClick={handleClose}
                    >
                        <SMBold textTransform={'none'}>{t('Cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>

                    <PrimaryButtonLG
                        fullWidth
                        size="large"
                        style={{ height: '39px' }}
                        id={'ignore-transaction'}
                        onClick={() => {
                            if (uiState == UI_STATE.LOOK_UP) {
                                /* empty */
                            } else {
                                cancelTransaction.mutate({
                                    bankTransactionRecordId:
                                        selectedTransaction?.bankTransactionRecordId!,
                                    transactionId: matchId
                                });
                            }
                        }}
                    >
                        <SMBold textTransform={'none'}>
                            {uiState == UI_STATE.LOOK_UP
                                ? t('Lookup')
                                : t(' Save and cancel')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Box>
            </StyledDialogContainer>
        </StyledDialog>
    );
};

export default CancelTransactionModel;
